import React, {useState, useRef, useEffect, useCallback, cloneElement} from 'react';
import {createPortal} from 'react-dom';
import {BaseTooltipProps, TooltipDelay} from './types';
import {calculateTooltipPosition} from '@/components/utils/positioning';
import {generateTooltipId, getAriaAttributes} from '@/components/utils/accessibility';

const BaseTooltip: React.FC<BaseTooltipProps> = ({
    content,
    placement = 'top',
    trigger = 'hover',
    delay = 0,
    disabled = false,
    className = '',
    id,
    children,
    offset = [0, 8],
    arrow = true,
    interactive = false,
    maxWidth = 300,
    zIndex = 99999,
}) => {
    const [isVisible, setIsVisible] = useState(false);
    const [actualPlacement, setActualPlacement] = useState(placement);
    const tooltipRef = useRef<HTMLDivElement>(null);
    const targetRef = useRef<HTMLElement | null>(null) as React.MutableRefObject<HTMLElement | null>;
    const timeoutRef = useRef<NodeJS.Timeout>();
    const tooltipId = useRef(id || generateTooltipId());

    const normalizedDelay = typeof delay === 'number'
        ? {show: delay, hide: delay}
        : delay as TooltipDelay;

    const updatePosition = useCallback(() => {
        if (!tooltipRef.current || !targetRef.current || !isVisible) {return;}

        const tooltipDimensions = {
            width: tooltipRef.current.offsetWidth,
            height: tooltipRef.current.offsetHeight,
        };

        const {position, finalPlacement} = calculateTooltipPosition(
            targetRef.current,
            tooltipDimensions,
            placement,
            offset
        );

        tooltipRef.current.style.left = `${position.left}px`;
        tooltipRef.current.style.top = `${position.top}px`;
        setActualPlacement(finalPlacement);
    }, [isVisible, placement, offset]);

    const showTooltip = useCallback(() => {
        if (disabled) {return;}

        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }

        timeoutRef.current = setTimeout(() => {
            setIsVisible(true);
        }, normalizedDelay.show);
    }, [disabled, normalizedDelay.show]);

    const hideTooltip = useCallback(() => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }

        timeoutRef.current = setTimeout(() => {
            setIsVisible(false);
        }, normalizedDelay.hide);
    }, [normalizedDelay.hide]);

    const handleMouseEnter = useCallback(() => {
        if (trigger === 'hover') {
            showTooltip();
        }
    }, [trigger, showTooltip]);

    const handleMouseLeave = useCallback(() => {
        if (trigger === 'hover') {
            hideTooltip();
        }
    }, [trigger, hideTooltip]);

    const handleClick = useCallback(() => {
        if (trigger === 'click') {
            // Click-controlled tooltip: toggle visibility
            if (isVisible) {
                hideTooltip();
            } else {
                showTooltip();
            }
        } else if (trigger === 'hover') {
            // For hover tooltips, explicitly hide on click (e.g. dropdown toggles)
            hideTooltip();
        }
    }, [trigger, isVisible, showTooltip, hideTooltip]);

    const handleFocus = useCallback(() => {
        if (trigger === 'focus') {
            showTooltip();
        }
    }, [trigger, showTooltip]);

    const handleBlur = useCallback(() => {
        if (trigger === 'focus') {
            hideTooltip();
        }
    }, [trigger, hideTooltip]);

    const handleKeyDown = useCallback((event: React.KeyboardEvent) => {
        if (event.key === 'Escape' && isVisible) {
            hideTooltip();
        }
    }, [isVisible, hideTooltip]);

    useEffect(() => {
        updatePosition();
    }, [updatePosition]);

    useEffect(() => {
        const handleResize = () => updatePosition();
        const handleScroll = () => updatePosition();

        if (isVisible) {
            window.addEventListener('resize', handleResize);
            window.addEventListener('scroll', handleScroll, true);

            return () => {
                window.removeEventListener('resize', handleResize);
                window.removeEventListener('scroll', handleScroll, true);
            };
        }
    }, [isVisible, updatePosition]);

    useEffect(() => {
        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        };
    }, []);

    const ariaAttributes = getAriaAttributes(tooltipId.current, isVisible);

    // Preserve existing child event handlers and refs while enhancing with tooltip behaviour,
    // so components like Dropdown.Toggle continue to work correctly.
    const childProps = children.props || {};
    // Access the original ref passed to the child (e.g. from react-bootstrap Dropdown.Toggle)
    const originalRef = (children as React.ReactElement & { ref?: React.Ref<HTMLElement> }).ref;

    const enhancedChild = cloneElement(children, {
        ref: (node: HTMLElement | null) => {
            // Store reference for tooltip positioning
            targetRef.current = node as HTMLElement | null;

            // Forward to child's original ref, if any
            if (typeof originalRef === 'function') {
                originalRef(node);
            } else if (originalRef && typeof originalRef === 'object') {
                // If the child uses an object ref, we deliberately do not overwrite it here
                // to avoid type-level issues; function refs are the primary integration path.
            }
        },
        onMouseEnter: (event: React.MouseEvent<HTMLElement>) => {
            if (childProps.onMouseEnter) {
                childProps.onMouseEnter(event);
            }
            handleMouseEnter();
        },
        onMouseLeave: (event: React.MouseEvent<HTMLElement>) => {
            if (childProps.onMouseLeave) {
                childProps.onMouseLeave(event);
            }
            handleMouseLeave();
        },
        onClick: (event: React.MouseEvent<HTMLElement>) => {
            if (childProps.onClick) {
                childProps.onClick(event);
            }
            handleClick();
        },
        onFocus: (event: React.FocusEvent<HTMLElement>) => {
            if (childProps.onFocus) {
                childProps.onFocus(event);
            }
            handleFocus();
        },
        onBlur: (event: React.FocusEvent<HTMLElement>) => {
            if (childProps.onBlur) {
                childProps.onBlur(event);
            }
            handleBlur();
        },
        onKeyDown: (event: React.KeyboardEvent<HTMLElement>) => {
            if (childProps.onKeyDown) {
                childProps.onKeyDown(event);
            }
            handleKeyDown(event);
        },
        ...ariaAttributes,
    });

    return (
        <>
            {enhancedChild}
            {isVisible &&
        createPortal(
            <div
                ref={tooltipRef}
                id={tooltipId.current}
                className={`unified-tooltip unified-tooltip--${actualPlacement} ${className}`}
                style={{
                    position: 'fixed',
                    zIndex,
                    maxWidth,
                    pointerEvents: interactive ? 'auto' : 'none',
                }}
                role="tooltip"
                onMouseEnter={interactive ? showTooltip : undefined}
                onMouseLeave={interactive ? hideTooltip : undefined}
            >
                <div className="unified-tooltip__content">
                    {content}
                </div>
                {arrow && (
                    <div className={`unified-tooltip__arrow unified-tooltip__arrow--${actualPlacement}`} />
                )}
            </div>,
            document.body
        )}
        </>
    );
};

export default BaseTooltip;
